import { EnvelopeIcon } from '@/assets/icons/envelope';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { Investment } from '@/components/dealCatalog-page/types';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { ALL_ADVISORY_FIRM_LIST_QUERY } from '@/components/investors-page/queries';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { getSettingsItemValue } from '@/components/settings-page';
import { SYSTEM_SETTINGS_QUERY } from '@/components/settings-page/queries';
import { SORT_ORDER } from '@/components/table/types';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { defaultListData } from '../modify/constants';
import { IListData } from '../modify/types';
import { Tenant } from '../types';
import { MANAGER_SPOTLIGHT_EMAIL_MUTATION, MANAGER_SPOTLIGHT_EMAIL_PREVIEW_QUERY, MANAGER_SPOTLIGHT_INVESTMENTS_QUERY } from './queries';
import { AdvisoryFirmsStep } from './steps/advisoryFirms';
import { IMessageStepForm, MessageStep } from './steps/message';
import { PreviewStep } from './steps/preview';
import { StrategiesStep } from './steps/strategies';

const initialManagerSpotlightWizardSteps = [
  { name: 'Advisory Firms', status: 'IN_PROGRESS' },
  { name: 'Strategies', status: 'NOT_STARTED' },
  { name: 'Message', status: 'NOT_STARTED' },
  { name: 'Preview', status: 'NOT_STARTED' }
];

const defaultSubjectValue = 'Manager Spotlight: New Opportunities for Investors';
const defaultMessageToInvestorValue =
  'We are excited to introduce new opportunities for investors. Visit the portal to learn more about this and other opportunities.';

export const advisoryFirmsStepRadioOptions = ['Send To All', 'Select Advisory Firms'];

export const ManagerSpotlightWizard = () => {
  const { user } = useAuth();
  const goBack = useGoBack();
  const navigate = useNavigate();
  const location = useLocation();
  const { default: defaultErrorMessage } = useErrorMessages();
  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';

  const [managerSpotlightWizardSteps, setManagerSpotlightWizardSteps] = useState(initialManagerSpotlightWizardSteps);
  const [selectedAdvisoryFirmsStepRadioOption, setSelectedAdvisoryFirmsStepRadioOption] = useState<string>(advisoryFirmsStepRadioOptions[0]);
  const [advisoryFirms, setAdvisoryFirms] = useState<IListData>(defaultListData);
  const [strategies, setStrategies] = useState<IListData>(defaultListData);
  const [firmInfoData, setFirmInfoData] = useState<any>({});
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [investmentIds, setInvestmentIds] = useState<string[]>([]);
  const [emailPreviewData, setEmailPreviewData] = useState<any>({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [messageInfo, setMessageInfo] = useState<IMessageStepForm>({
    subject: defaultSubjectValue,
    messageToInvestor: defaultMessageToInvestorValue
  });

  const [managerSpotlightEmail, { loading: managerSpotlightEmailLoading }] = useMutation(MANAGER_SPOTLIGHT_EMAIL_MUTATION);

  const { data: advisoryFirmListData, loading: advisoryFirmListLoading } = useQuery<{ allAdvisoryFirmList: Tenant[] }>(ALL_ADVISORY_FIRM_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      data: {
        sortBy: 'NAME',
        sortDirection: SORT_ORDER.ASC
      }
    },
    onCompleted: (data) => {
      const advisoryFirmList = data.allAdvisoryFirmList.map((advisoryFirm) => advisoryFirm.name);
      setAdvisoryFirms({ ...advisoryFirms, list: advisoryFirmList });
    }
  });

  const { data: investmentsData, loading: investmentsLoading } = useQuery<{ managerSpotlightInvestments: Investment[] }>(
    MANAGER_SPOTLIGHT_INVESTMENTS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      skip: !tenantIds.length,
      variables: {
        tenantIds
      },
      onCompleted: (data) => {
        const strategiesList = data.managerSpotlightInvestments.map((strategy) => strategy.name);
        setStrategies({ ...strategies, chosen: [], list: strategiesList });
      },
      onError: (error) => {
        console.error(error);
      }
    }
  );

  const { loading: managerSpotlightEmailPreviewLoading } = useQuery(MANAGER_SPOTLIGHT_EMAIL_PREVIEW_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !tenantIds.length || !investmentIds.length || managerSpotlightWizardSteps?.[3]?.status !== 'IN_PROGRESS',
    variables: {
      data: {
        tenantIds,
        investmentIds,
        ...messageInfo
      }
    },
    onCompleted: (data) => {
      setTotalUserCount(data?.managerSpotlightEmailPreview?.totalUserCount ?? 0);
      setEmailPreviewData(data?.managerSpotlightEmailPreview?.previewData?.[0]?.substitution_data ?? {});
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: systemSettingsLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'config'
      }
    },
    onCompleted: (data) => {
      const firmInfo = getSettingsItemValue(data?.systemSettings ?? [], 'firmInfo') || {};
      setFirmInfoData(firmInfo);
    }
  });

  useEffect(() => {
    if (!Object.values(location?.state || {}).length || !advisoryFirmListData) return;
    const { selectedAdvisoryFirmsStepRadioOption, chosenAdvisoryFirms, chosenStrategies, messageInfo } = location.state || {};

    if (selectedAdvisoryFirmsStepRadioOption) {
      setSelectedAdvisoryFirmsStepRadioOption(selectedAdvisoryFirmsStepRadioOption);
    }

    setAdvisoryFirms((prev) => ({
      ...prev,
      chosen: chosenAdvisoryFirms || []
    }));
    handleTenantIds(chosenAdvisoryFirms || [], selectedAdvisoryFirmsStepRadioOption);

    if (messageInfo) {
      setMessageInfo((prev) => ({
        ...prev,
        subject: messageInfo?.subject || defaultSubjectValue,
        messageToInvestor: messageInfo?.messageToInvestor || defaultMessageToInvestorValue
      }));
    }

    if (!investmentsData) return;

    if (chosenStrategies?.length) {
      setStrategies((prev) => ({
        ...prev,
        chosen: chosenStrategies
      }));
      handleInvestmentIds(chosenStrategies);
    }

    if (Object.values(location?.state || {}).length) {
      const updatedSteps = managerSpotlightWizardSteps.map((step, index) => {
        if (index === managerSpotlightWizardSteps.length - 1) {
          return { ...step, status: 'IN_PROGRESS' };
        }
        return { ...step, status: 'COMPLETED' };
      });

      setManagerSpotlightWizardSteps(updatedSteps);
      navigate('.', { replace: true, state: null });
    }
  }, [location.state, advisoryFirmListData, investmentsData]);

  const handleTenantIds = (chosenAdvisoryFirms: string[], selectedAdvisoryFirmsStepRadioOption: string) => {
    if (selectedAdvisoryFirmsStepRadioOption === advisoryFirmsStepRadioOptions[0]) {
      const tenantIds = advisoryFirmListData?.allAdvisoryFirmList?.map((advisoryFirm) => advisoryFirm.id);
      setTenantIds(tenantIds);
    } else if (selectedAdvisoryFirmsStepRadioOption === advisoryFirmsStepRadioOptions[1] && chosenAdvisoryFirms.length) {
      const filteredAdvisoryFirms = advisoryFirmListData?.allAdvisoryFirmList?.filter((advisoryFirm) =>
        chosenAdvisoryFirms.includes(advisoryFirm.name)
      );
      const tenantIds = filteredAdvisoryFirms?.map((advisoryFirm) => advisoryFirm.id);
      setTenantIds(tenantIds);
    }
  };

  const handleInvestmentIds = (chosenStrategies: string[]) => {
    const investmentIds = investmentsData?.managerSpotlightInvestments
      ?.filter((strategy) => chosenStrategies.includes(strategy.name))
      ?.map((strategy) => strategy.id);
    setInvestmentIds(investmentIds);
  };

  const handleManagerSpotlightWizardSteps = (steps: { name: string; status: string }[]) => {
    setManagerSpotlightWizardSteps(steps);
  };

  const saveMessageInfo = ({ subject, messageToInvestor }: IMessageStepForm) => {
    setMessageInfo({ subject, messageToInvestor });
  };

  const openPage = (path: string) => {
    navigate('.', {
      replace: true,
      state: { selectedAdvisoryFirmsStepRadioOption, chosenAdvisoryFirms: advisoryFirms.chosen, chosenStrategies: strategies.chosen, messageInfo }
    });
    navigate(path);
  };

  const sendEmail = async () => {
    try {
      setErrorMessage(null);

      await managerSpotlightEmail({
        variables: {
          data: {
            tenantIds,
            investmentIds,
            ...messageInfo
          }
        }
      });

      setShowSuccessDialog(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (advisoryFirmListLoading || systemSettingsLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {(investmentsLoading || managerSpotlightEmailPreviewLoading || managerSpotlightEmailLoading) && (
        <ModalWrappedContent
          isOpen={investmentsLoading || managerSpotlightEmailPreviewLoading || managerSpotlightEmailLoading}
          title={managerSpotlightEmailLoading ? 'Saving' : 'Loading'}
          showCancelButton={false}
          showCloseIcon={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {showSuccessDialog && (
        <ModalWrappedContent
          isOpen={showSuccessDialog}
          onClose={() => goBack({ fallBack: '/dealPipeline' })}
          showCancelButton={false}
          isUseOutsideClick={false}
          confirmButton={{ name: 'OK', onClick: () => goBack({ fallBack: '/dealPipeline' }) }}
        >
          <ModalBody>
            <ApprovedIcon width={60} height={60} />
            <ModalTitle>Sent Successfully</ModalTitle>
            <ModalDescription>Manager Spotlight successfully sent to {totalUserCount} users.</ModalDescription>
          </ModalBody>
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={() => goBack({ fallBack: '/dealPipeline' })} backToTitle={backToTitle} />} />
        <PageTitle title="Manager Spotlight" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
      </MainWrap>
      <MainWrap>
        {managerSpotlightWizardSteps?.[3]?.status === 'IN_PROGRESS' && !managerSpotlightEmailPreviewLoading && (
          <SubHeader>
            <EnvelopeIcon fill={themeVar('font.base')} />
            Sending Email Notification to {totalUserCount} Users
          </SubHeader>
        )}
        <PaddingWrap>
          {managerSpotlightWizardSteps?.[0]?.status === 'IN_PROGRESS' && (
            <AdvisoryFirmsStep
              managerSpotlightWizardSteps={managerSpotlightWizardSteps}
              selectedAdvisoryFirmsStepRadioOption={selectedAdvisoryFirmsStepRadioOption}
              advisoryFirms={advisoryFirms}
              setAdvisoryFirms={setAdvisoryFirms}
              handleTenantIds={handleTenantIds}
              setSelectedAdvisoryFirmsStepRadioOption={setSelectedAdvisoryFirmsStepRadioOption}
              handleManagerSpotlightWizardSteps={handleManagerSpotlightWizardSteps}
            />
          )}
          {managerSpotlightWizardSteps?.[1]?.status === 'IN_PROGRESS' && (
            <StrategiesStep
              managerSpotlightWizardSteps={managerSpotlightWizardSteps}
              strategies={strategies}
              setStrategies={setStrategies}
              handleInvestmentIds={handleInvestmentIds}
              handleManagerSpotlightWizardSteps={handleManagerSpotlightWizardSteps}
            />
          )}
          {managerSpotlightWizardSteps?.[2]?.status === 'IN_PROGRESS' && (
            <MessageStep
              managerSpotlightWizardSteps={managerSpotlightWizardSteps}
              messageInfo={messageInfo}
              saveMessageInfo={saveMessageInfo}
              handleManagerSpotlightWizardSteps={handleManagerSpotlightWizardSteps}
            />
          )}
          {managerSpotlightWizardSteps?.[3]?.status === 'IN_PROGRESS' && (
            <PreviewStep
              managerSpotlightWizardSteps={managerSpotlightWizardSteps}
              firmInfoData={firmInfoData}
              totalUserCount={totalUserCount}
              emailPreviewData={emailPreviewData}
              openPage={openPage}
              sendEmail={sendEmail}
              handleManagerSpotlightWizardSteps={handleManagerSpotlightWizardSteps}
              managerSpotlightEmailPreviewLoading={managerSpotlightEmailPreviewLoading}
            />
          )}
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export const ManagerSpotlightWizardStepWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 30px 0 150px 0;
  padding: ${({ isMobile }) => (isMobile ? '20px 30px' : '40px 60px')};
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 19px;
  font-weight: 600;
  line-height: 22px;
  color: ${themeVar('font.strong')};
  padding: 24px 60px;
  background-color: ${themeVar('layer.1')};
  border-bottom: 1px solid ${themeVar('border.base')};
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ModalTitle = styled.h2`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-align: center;
  color: ${themeVar('font.base')};
`;
